/**
 * Panel de supervisión: datos habituales de la célula + historial ~2 meses.
 */
import { useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  buildMeetingHistoryRows,
  cellScheduleSummaryLabels,
  complianceFromRows,
  formatMeetingDateDisplay,
  generateRecentHabitualDates,
} from "@/lib/cellMeetingSchedule";

/**
 * @param {{
 *   cell: object|null,
 *   leaderName?: string,
 *   entries?: Array<{meeting_date: string, scheduled_meeting_date?: string|null, meeting_suspended?: boolean}>,
 *   loading?: boolean,
 *   selectedDate?: string,
 *   onEnterReport?: (actualDate: string, scheduledDate: string) => void,
 *   onViewReport?: (actualDate: string, scheduledDate: string) => void,
 *   reportNoun?: string,
 * }} props
 */
export function CellMeetingCalendarPanel({
  cell,
  leaderName = "Sin asignar",
  entries = [],
  loading = false,
  selectedDate = "",
  onEnterReport,
  onViewReport,
  reportNoun = "reporte",
}) {
  const [exceptionFor, setExceptionFor] = useState(null); // scheduled date
  const [exceptionDate, setExceptionDate] = useState("");

  const labels = cellScheduleSummaryLabels(cell);
  const hasDay = Boolean(cell?.meeting_day);

  const rows = useMemo(() => {
    if (!hasDay) return [];
    const dates = generateRecentHabitualDates(cell.meeting_day, 8);
    return buildMeetingHistoryRows(dates, entries, cell?.meeting_time);
  }, [cell, entries, hasDay]);

  const compliance = useMemo(() => complianceFromRows(rows), [rows]);

  if (!cell) return null;

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg text-slate-900">Calendario e historial de reuniones</CardTitle>
        <CardDescription>
          Basado en el día, hora y modalidad configurados en la célula. Últimas {compliance.total || 8}{" "}
          reuniones habituales.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm space-y-1">
          <p>
            <span className="font-semibold text-slate-800">Célula: </span>
            <span className="text-slate-900">{cell.name || "Sin nombre"}</span>
          </p>
          <p>
            <span className="font-semibold text-slate-800">Líder: </span>
            <span className="text-slate-800">{leaderName || "Sin asignar"}</span>
          </p>
          <p>
            <span className="font-semibold text-slate-800">Día habitual: </span>
            {labels.dayLabel}
          </p>
          <p>
            <span className="font-semibold text-slate-800">Hora: </span>
            {labels.timeLabel}
          </p>
          <p>
            <span className="font-semibold text-slate-800">Modalidad: </span>
            {labels.modalityLabel}
          </p>
        </div>

        {!hasDay ? (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
            Esta célula aún no tiene día de reunión configurado. Configure el día, hora y modalidad en
            Células para generar el calendario automático.
          </p>
        ) : (
          <>
            <div className="rounded-md border border-blue-100 bg-blue-50/60 px-3 py-2 text-sm text-blue-950">
              <p className="font-medium">
                Últimas {compliance.total} reuniones — {compliance.entered} de {compliance.total}{" "}
                {reportNoun}
                {compliance.entered === 1 ? "" : "s"} ingresado
                {compliance.entered === 1 ? "" : "s"} — {compliance.pct}% de cumplimiento
              </p>
              <p className="text-xs text-blue-800 mt-0.5">
                Ingresados: {compliance.entered} · Pendientes: {compliance.pending}
              </p>
            </div>

            {loading ? (
              <p className="text-sm text-gray-500 py-4 text-center">Cargando historial…</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-gray-200">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="text-left p-2.5 font-semibold text-gray-700">Fecha de reunión</th>
                      <th className="text-left p-2.5 font-semibold text-gray-700">Día</th>
                      <th className="text-left p-2.5 font-semibold text-gray-700">Hora</th>
                      <th className="text-left p-2.5 font-semibold text-gray-700">Estado</th>
                      <th className="text-left p-2.5 font-semibold text-gray-700">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const isSelected =
                        selectedDate &&
                        (selectedDate === row.actualDate || selectedDate === row.scheduledDate);
                      return (
                        <tr
                          key={row.key}
                          className={`border-t border-gray-100 ${isSelected ? "bg-indigo-50/50" : "hover:bg-gray-50/80"}`}
                        >
                          <td className="p-2.5 text-gray-900">
                            <div className="font-medium">{formatMeetingDateDisplay(row.actualDate)}</div>
                            {row.exceptional ? (
                              <div className="text-xs text-amber-800 mt-0.5">
                                Habitual: {formatMeetingDateDisplay(row.scheduledDate)} · realizada:{" "}
                                {formatMeetingDateDisplay(row.actualDate)}
                              </div>
                            ) : null}
                          </td>
                          <td className="p-2.5 text-gray-700">{row.dayLabel}</td>
                          <td className="p-2.5 text-gray-700 tabular-nums">{row.timeLabel}</td>
                          <td className="p-2.5">
                            {row.status === "ingresado" ? (
                              <Badge
                                variant="outline"
                                className="bg-emerald-50 text-emerald-800 border-emerald-200 font-medium"
                              >
                                {row.suspended ? "Ingresado (suspendida)" : "Ingresado"}
                              </Badge>
                            ) : (
                              <Badge
                                variant="outline"
                                className="bg-amber-50 text-amber-900 border-amber-200 font-medium"
                              >
                                Pendiente
                              </Badge>
                            )}
                          </td>
                          <td className="p-2.5">
                            <div className="flex flex-col items-start gap-1">
                              {row.status === "ingresado" ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8"
                                  onClick={() => onViewReport?.(row.actualDate, row.scheduledDate)}
                                >
                                  Ver {reportNoun}
                                </Button>
                              ) : (
                                <>
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="h-8 bg-blue-600 hover:bg-blue-700"
                                    onClick={() => onEnterReport?.(row.scheduledDate, row.scheduledDate)}
                                  >
                                    Ingresar {reportNoun}
                                  </Button>
                                  {exceptionFor === row.scheduledDate ? (
                                    <div className="flex flex-wrap items-end gap-2 mt-1">
                                      <div className="space-y-1">
                                        <Label className="text-xs">Nueva fecha</Label>
                                        <Input
                                          type="date"
                                          className="h-8 w-auto"
                                          value={exceptionDate}
                                          onChange={(e) => setExceptionDate(e.target.value)}
                                        />
                                      </div>
                                      <Button
                                        type="button"
                                        size="sm"
                                        className="h-8"
                                        disabled={!exceptionDate}
                                        onClick={() => {
                                          if (!exceptionDate) return;
                                          onEnterReport?.(exceptionDate, row.scheduledDate);
                                          setExceptionFor(null);
                                          setExceptionDate("");
                                        }}
                                      >
                                        Usar esta fecha
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        className="h-8"
                                        onClick={() => {
                                          setExceptionFor(null);
                                          setExceptionDate("");
                                        }}
                                      >
                                        Cancelar
                                      </Button>
                                    </div>
                                  ) : (
                                    <button
                                      type="button"
                                      className="text-xs text-slate-600 underline-offset-2 hover:underline"
                                      onClick={() => {
                                        setExceptionFor(row.scheduledDate);
                                        setExceptionDate("");
                                      }}
                                    >
                                      Cambiar fecha de esta reunión
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
